/* eslint-disable max-lines */
import { useEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import type { TenderCase, TenderRun } from "../types"

import {
  downloadBidReviewDocx,
  downloadCvMatchDocx,
  downloadExtractionDocx,
  downloadPmDocx,
} from "@/api/tender-api"
import {
  ArrowLeft,
  ArrowRightLeft,
  Check,
  ChevronDown,
  CircleDashed,
  CircleDot,
  Download,
  LogOut,
  MessageSquare,
  MoreHorizontal,
  Pencil,
  Plus,
  Share2,
  Shield,
  X,
} from "@/components/icons"
import { ModulePageHeader } from "@/components/layouts/module-page-header"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import { useContentSidebarStore } from "@/stores/content-sidebar-store"

import { TenderCaseActionDialogs } from "./tender-case-action-dialogs"
import { deriveRunStatus, STATUS_CONFIG } from "./tender-status-utils"
import { useTenderPaths } from "../hooks/use-tender-paths"

const TENDER_CHAT_PANEL_ID = "tender-chat"

function runCompletionLevel(
  run: TenderRun,
  isBidReviewStale = false
): "full" | "partial" | "intake" {
  if (run.bidReviewStatus === "completed" && !isBidReviewStale) return "full"
  if (
    run.extractionStatus === "completed" ||
    run.extractionStatus === "partial" ||
    run.extractionStatus === "processing"
  )
    return "partial"
  return "intake"
}

const RUN_COMPLETION_ICON: Record<ReturnType<typeof runCompletionLevel>, React.ReactNode> = {
  full: <CircleDot className="text-success size-3.5" />,
  partial: <CircleDashed className="text-warning size-3.5" />,
  intake: <CircleDot className="text-muted-foreground/30 size-3.5" />,
}

type TenderHeaderProps = {
  tenderCase: TenderCase
  runs: TenderRun[]
  activeRunId: string
  isActiveRunBidReviewStale?: boolean
  isExtractionCompleted?: boolean
  isCvMatchCompleted?: boolean
  canEdit: boolean
  isOwner: boolean
  canManage: boolean
  onOpenChat: () => void
  onShare: () => void
  onHandoff: () => void
  onTitleChange: (title: string) => void
  onRunChange: (runId: string) => void
  onStartNewRun: () => void
}

export function TenderHeader({
  tenderCase,
  runs,
  activeRunId,
  isActiveRunBidReviewStale = false,
  isExtractionCompleted = false,
  isCvMatchCompleted = false,
  canEdit,
  isOwner,
  canManage,
  onOpenChat,
  onShare,
  onHandoff,
  onTitleChange,
  onRunChange,
  onStartNewRun,
}: TenderHeaderProps) {
  const { t, i18n } = useTranslation()
  const currentPanelId = useContentSidebarStore((state) => state.currentPanel?.id)
  const chatOpen = currentPanelId === TENDER_CHAT_PANEL_ID
  const navigate = useNavigate()
  const { basePath } = useTenderPaths()
  const activeRun = runs.find((r) => r.id === activeRunId)
  const latestRun = runs[runs.length - 1]
  const isViewingLatest = activeRunId === latestRun.id
  const statusConf = activeRun
    ? STATUS_CONFIG[
        deriveRunStatus(activeRun, tenderCase.status, {
          isBidReviewStale: isActiveRunBidReviewStale,
          handedOff: tenderCase.handedOffAt != null,
        })
      ]
    : STATUS_CONFIG.setup
  const [isRenamingTitle, setIsRenamingTitle] = useState(false)
  const [titleDraft, setTitleDraft] = useState(tenderCase.title)
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const isAdminFallback = tenderCase.currentUserAccessSource === "admin_default"

  const titleInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!isRenamingTitle) {
      setTitleDraft(tenderCase.title)
    }
  }, [isRenamingTitle, tenderCase.title])

  useEffect(() => {
    if (!isRenamingTitle) return

    const timeout = window.setTimeout(() => {
      titleInputRef.current?.focus()
      titleInputRef.current?.select()
    }, 0)

    return () => window.clearTimeout(timeout)
  }, [isRenamingTitle])

  const handleStartTitleRename = () => {
    if (isRenamingTitle) return
    setTitleDraft(tenderCase.title)
    setIsRenamingTitle(true)
  }

  const handleCancelTitleRename = () => {
    setTitleDraft(tenderCase.title)
    setIsRenamingTitle(false)
  }

  const handleSubmitTitleRename = () => {
    if (titleDraft.trim() === tenderCase.title.trim()) {
      setIsRenamingTitle(false)
      return
    }
    onTitleChange(titleDraft)
    setIsRenamingTitle(false)
  }

  return (
    <>
      <ModulePageHeader
        sticky
        meta={
          <>
            {tenderCase.reference && <span>{tenderCase.reference}</span>}
            {tenderCase.reference && <span>·</span>}
            <span>
              {t("tender.case.created")}{" "}
              {new Date(tenderCase.createdAt).toLocaleDateString(i18n.language, {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>
            <span>·</span>
            <span>
              {tenderCase.isTitleAutoGenerated
                ? t("tender.header.titleAutoGenerated")
                : t("tender.header.titleManual")}
            </span>
          </>
        }
      >
        <div className="flex items-center gap-2">
          <Button
            className="-ml-2 shrink-0"
            size="sm"
            variant="ghost"
            onClick={() => void navigate(basePath)}
          >
            <ArrowLeft className="mr-1 size-4" />
            {t("tender.actions.back")}
          </Button>

          {/* Title — fills available space, only truncates when truly necessary */}
          <div className="min-w-0 flex-1">
            {isRenamingTitle ? (
              <form
                className="flex items-center gap-1"
                onSubmit={(event) => {
                  event.preventDefault()
                  handleSubmitTitleRename()
                }}
              >
                <Input
                  ref={titleInputRef}
                  aria-label={t("tender.header.renameAria")}
                  className="h-8 min-w-0 text-sm font-semibold"
                  maxLength={120}
                  style={{ width: `${Math.max(titleDraft.length + 2, 12)}ch` }}
                  value={titleDraft}
                  onChange={(event) => setTitleDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Escape") {
                      event.preventDefault()
                      handleCancelTitleRename()
                    }
                  }}
                />
                <Button
                  aria-label={t("tender.header.confirmNameAria")}
                  className="h-8 w-8 shrink-0"
                  disabled={titleDraft.trim().length === 0}
                  size="icon"
                  type="submit"
                  variant="ghost"
                >
                  <Check className="size-4" />
                </Button>
                <Button
                  aria-label={t("tender.header.cancelRenameAria")}
                  className="h-8 w-8 shrink-0"
                  size="icon"
                  type="button"
                  variant="ghost"
                  onClick={handleCancelTitleRename}
                >
                  <X className="size-4" />
                </Button>
              </form>
            ) : (
              <h1 className="min-w-0 text-base font-semibold">
                {canEdit ? (
                  <Button
                    className="group/title hover:bg-muted/60 focus-visible:ring-ring -mx-2 h-auto max-w-full gap-1.5 px-2 py-1 font-semibold"
                    title={t("tender.header.titleClickHint")}
                    type="button"
                    variant="ghost"
                    onClick={handleStartTitleRename}
                  >
                    <span className="truncate">{tenderCase.title}</span>
                    <Pencil className="text-muted-foreground size-3.5 shrink-0 opacity-0 transition-opacity group-focus-within/title:opacity-100 group-hover/title:opacity-100" />
                  </Button>
                ) : (
                  <span className="-mx-2 inline-flex max-w-full items-center gap-1.5 px-2 py-1">
                    <span className="truncate">{tenderCase.title}</span>
                  </span>
                )}
              </h1>
            )}
          </div>

          <Badge
            className={cn("shrink-0 border font-normal", statusConf.className)}
            variant="outline"
          >
            {t(statusConf.labelKey)}
          </Badge>
          {isAdminFallback && (
            <div
              className="text-muted-foreground border-warning bg-warning/10 flex items-center gap-2 rounded-md border px-2.5 py-1 text-xs"
              title={t("tender.header.adminFallbackTitle")}
            >
              <Badge
                className="border-warning bg-warning/20 text-warning-foreground hover:bg-warning/20 font-normal"
                variant="outline"
              >
                <Shield className="mr-1 size-3" />
                {t("tender.header.adminAccessBadge")}
              </Badge>
              <span className="hidden lg:inline">{t("tender.header.adminFallbackShort")}</span>
            </div>
          )}

          {/* Run switcher */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                type="button"
                variant="outline"
                className={cn(
                  "h-auto shrink-0 gap-1 px-2 py-0.5 text-xs font-medium",
                  isViewingLatest
                    ? "hover:bg-muted/60"
                    : "text-muted-foreground hover:bg-muted/40 border-dashed"
                )}
              >
                {activeRun
                  ? t("tender.header.runNumber", { n: activeRun.runNumber })
                  : t("tender.header.run")}
                <ChevronDown className="size-3 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-60">
              {/* Latest run — full style */}
              {(() => {
                const level = runCompletionLevel(
                  latestRun,
                  latestRun.id === activeRunId ? isActiveRunBidReviewStale : false
                )
                const isActive = latestRun.id === activeRunId
                return (
                  <DropdownMenuItem
                    key={latestRun.id}
                    className="flex items-center gap-2"
                    onSelect={() => onRunChange(latestRun.id)}
                  >
                    {RUN_COMPLETION_ICON[level]}
                    <span className={cn("flex-1", isActive && "font-semibold")}>
                      {t("tender.header.runNumber", { n: latestRun.runNumber })}
                    </span>
                    <span className="text-muted-foreground text-xs">
                      {new Date(latestRun.createdAt).toLocaleDateString(i18n.language, {
                        month: "short",
                        day: "numeric",
                      })}
                    </span>
                    {isActive && <Check className="text-success size-3.5 shrink-0" />}
                  </DropdownMenuItem>
                )
              })()}

              {/* Older (archived) runs — shown if there are any */}
              {runs.length > 1 && (
                <>
                  <DropdownMenuSeparator />
                  <p className="text-muted-foreground/60 px-2 py-1 text-[10px] font-semibold tracking-widest uppercase">
                    {t("tender.header.previousRuns")}
                  </p>
                  {[...runs]
                    .slice(0, -1)
                    .reverse()
                    .map((run) => {
                      const level = runCompletionLevel(
                        run,
                        run.id === activeRunId ? isActiveRunBidReviewStale : false
                      )
                      const isActive = run.id === activeRunId
                      return (
                        <DropdownMenuItem
                          key={run.id}
                          className="flex items-center gap-2"
                          onSelect={() => onRunChange(run.id)}
                        >
                          <span className="opacity-40">{RUN_COMPLETION_ICON[level]}</span>
                          <span
                            className={cn(
                              "text-muted-foreground flex-1 text-xs",
                              isActive && "font-semibold"
                            )}
                          >
                            {t("tender.header.runNumber", { n: run.runNumber })}
                          </span>
                          <span className="text-muted-foreground/60 text-xs">
                            {new Date(run.createdAt).toLocaleDateString(i18n.language, {
                              month: "short",
                              day: "numeric",
                            })}
                          </span>
                          {isActive && <Check className="text-success size-3.5 shrink-0" />}
                        </DropdownMenuItem>
                      )
                    })}
                </>
              )}

              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-muted-foreground gap-2"
                disabled={!canEdit}
                onSelect={canEdit ? onStartNewRun : undefined}
              >
                <Plus className="size-3.5" />
                {t("tender.header.startNewRun")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="flex shrink-0 items-center gap-2">
            {tenderCase.handedOffAt && (
              <span className="text-muted-foreground bg-muted inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium">
                <ArrowRightLeft className="size-3.5" />
                {t("tender.header.handedOff")}
              </span>
            )}
            {canManage && (
              <Button
                className="gap-1.5"
                size="sm"
                variant={isExtractionCompleted && !tenderCase.handedOffAt ? "default" : "outline"}
                onClick={onHandoff}
              >
                <ArrowRightLeft className="size-4" />
                {t(tenderCase.handedOffAt ? "tender.summaryEmail.title" : "tender.header.handoff")}
              </Button>
            )}
            {tenderCase.currentUserRole && (
              <Button className="gap-1.5" size="sm" variant="outline" onClick={onShare}>
                <Share2 className="size-4" />
                {t("tender.header.share")}
              </Button>
            )}
            <Button
              className="gap-1.5"
              size="sm"
              variant={chatOpen ? "secondary" : "outline"}
              onClick={chatOpen ? () => useContentSidebarStore.getState().closePanel() : onOpenChat}
            >
              <MessageSquare className="size-4" />
              {t("tender.header.chatWithCase")}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="gap-1.5" size="sm" variant="outline">
                  <Download className="size-4" />
                  {t("tender.actions.export")}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  disabled={!isExtractionCompleted}
                  onSelect={() => void downloadPmDocx(tenderCase.id, activeRunId, tenderCase.title)}
                >
                  {t("tender.header.exportPmDocx")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={!isExtractionCompleted}
                  onSelect={() =>
                    void downloadExtractionDocx(tenderCase.id, activeRunId, tenderCase.title)
                  }
                >
                  {t("tender.header.exportExtractionDocx")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={!isCvMatchCompleted}
                  onSelect={() =>
                    void downloadCvMatchDocx(tenderCase.id, activeRunId, tenderCase.title)
                  }
                >
                  {t("tender.header.exportCvMatchDocx")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={activeRun?.bidReviewStatus !== "completed" || isActiveRunBidReviewStale}
                  onSelect={() =>
                    void downloadBidReviewDocx(tenderCase.id, activeRunId, tenderCase.title)
                  }
                >
                  {t("tender.header.exportBidReviewDocx")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="icon" variant="ghost">
                  <MoreHorizontal className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  disabled={!canEdit}
                  onSelect={canEdit ? handleStartTitleRename : undefined}
                >
                  {t("tender.actions.rename")}
                </DropdownMenuItem>
                <DropdownMenuItem disabled>{t("tender.actions.duplicate")}</DropdownMenuItem>
                {!isOwner && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive gap-2"
                      onSelect={() => setLeaveDialogOpen(true)}
                    >
                      <LogOut className="size-4" />
                      {t("tender.header.leaveCase")}
                    </DropdownMenuItem>
                  </>
                )}
                {isOwner && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive gap-2"
                      onSelect={() => setDeleteDialogOpen(true)}
                    >
                      {t("tender.actions.delete")}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </ModulePageHeader>

      <TenderCaseActionDialogs
        deleteDialogOpen={deleteDialogOpen}
        leaveDialogOpen={leaveDialogOpen}
        tenderCase={tenderCase}
        onDeleteOpenChange={setDeleteDialogOpen}
        onLeaveOpenChange={setLeaveDialogOpen}
      />
    </>
  )
}
